import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import uuid from "uuid-random";
import { toast } from "@foris/avocado-suite";
import {
  retryApprovalFlowAbilities,
  setActiveProposal,
  setOpenedProposalRequest,
  useAssignmentProposalStore,
} from "@context/useAssignmentProposalStore";
import type { AssignmentProposalErrorCode, Section } from "@models/ISchema";
import type { IParams } from "@models/IParams";
import { toActiveProposal } from "@services/collections/assignment-proposal/assignmentProposalAdapters";
import { useProposalResourcesLoader } from "@services/collections/assignment-proposal/assignmentProposalResourceServices";
import {
  useApprovalFlowRoles,
  useAssignmentProposalLoader,
} from "@services/collections/assignment-proposal/assignmentProposalServices";
import { AppContext } from "../context/EditSessionsContext";
import { Types } from "../context/formData.reducer";
import { buildAssignationChangesets, changesetsFingerprint } from "../utils/assignationChangesets";
import {
  buildFormStateFromProposal,
  collectLinkResources,
  collectSnapshotResourceIds,
  isLinkScheduleSnapshot,
} from "../utils/proposalSnapshot";

/**
 * - "none": the editor was not opened for a proposal (or the user cannot have any): regular entry.
 * - "loading": the proposal is being requested or applied; the editor must not render yet.
 * - "ready": the proposal is applied as the editor's pending changes.
 * - "error": it could not be loaded, or the roles to open it could not; the editor must show the
 *   error instead of the form.
 */
export type OpenedProposalStatus = "none" | "loading" | "ready" | "error";

/**
 * Opens the editor for the proposal the URL names: as its requester or, with `isReview`, as its
 * reviewer, and only for a user with that role. The proposal is applied over the link's data once
 * per load of that data, and not again when a save swaps the URL to the id the editor holds.
 */
export const useOpenedProposal = (proposalId: string | null, isReview = false) => {
  const { state, dispatch } = useContext(AppContext);
  const { scenario, origin }: IParams = useParams();
  const { t } = useTranslation();
  const loadProposal = useAssignmentProposalLoader();
  const loadResources = useProposalResourcesLoader();
  const { canSubmit, canReview, isResolving, hasAbilitiesError } = useApprovalFlowRoles();
  const request = useAssignmentProposalStore(store => store.openedProposalRequest);
  const hasRole = isReview ? canReview : canSubmit;

  const linkId = state?.link?.info?.id;
  const sections = state?.link?.sections;
  const weeks = state?.link?.weeks;

  const [attempt, setAttempt] = useState(0);
  // The link data the proposal was applied over: a reload of the link needs it applied again.
  const appliedOver = useRef<Section[] | null>(null);

  const isIgnored = !proposalId || (!isResolving && !hasRole);
  const isWaiting = isResolving || !linkId || !sections || !request;

  let status: OpenedProposalStatus = "loading";
  if (isIgnored) status = "none";
  // Without the roles the proposal cannot be opened: the error offers to ask them again.
  else if (hasAbilitiesError) status = "error";
  else if (!isWaiting) status = request.status === "loaded" ? "ready" : request.status;

  useEffect(() => {
    if (!proposalId || isResolving || !hasRole || !linkId || !sections) return undefined;

    const isAlreadyApplied =
      appliedOver.current === sections &&
      useAssignmentProposalStore.getState().activeProposal?.proposalId === proposalId;
    if (isAlreadyApplied) return undefined;

    let isActive = true;
    const opened = { linkId: String(linkId), proposalId };
    const fail = (code: AssignmentProposalErrorCode | null) =>
      setOpenedProposalRequest({ ...opened, status: "error", errorCode: code });

    const open = async () => {
      // Written first: from now on the editor runs in proposal mode, so the proposal's content can
      // never reach a direct save.
      setOpenedProposalRequest({ ...opened, status: "loading", errorCode: null });

      const outcome = await loadProposal({
        scenarioId: scenario,
        originId: origin,
        isReview,
        ...opened,
      });
      if (!isActive) return;

      if (outcome.kind !== "loaded") {
        fail(outcome.kind === "rejected" ? outcome.errorCode : null);
        return;
      }

      const snapshot = outcome.content;
      // Never apply something that is not a snapshot: every session would look removed.
      if (!isLinkScheduleSnapshot(snapshot)) {
        console.error("[OpenedProposal] unexpected content", snapshot);
        fail(null);
        return;
      }

      const linkResources = collectLinkResources(sections);
      const snapshotIds = collectSnapshotResourceIds(snapshot);
      let requested: Awaited<ReturnType<typeof loadResources>>;

      try {
        requested = await loadResources(
          { scenarioId: scenario, originId: origin },
          {
            classroomIds: snapshotIds.classroomIds.filter(id => !linkResources.classroomsById[id]),
            instructorIds: snapshotIds.instructorIds.filter(
              id => !linkResources.instructorsById[id],
            ),
          },
        );
      } catch (error) {
        console.error("[OpenedProposal] resources", error);
        if (isActive) fail(null);
        return;
      }
      if (!isActive) return;

      const { firstChangedSession, hasSkippedSections, ...changes } = buildFormStateFromProposal({
        snapshot,
        sections,
        weeks: weeks ?? [],
        classroomsById: { ...requested.classroomsById, ...linkResources.classroomsById },
        instructorsById: { ...requested.instructorsById, ...linkResources.instructorsById },
        createId: uuid,
      });

      dispatch({ type: Types.LoadProposalChanges, payload: changes });
      setActiveProposal(
        toActiveProposal({
          linkId: opened.linkId,
          proposal: outcome.proposal,
          rejectionReason: outcome.rejectionReason,
          isReview,
          // What the editor holds before the reviewer touches anything.
          loadedChangesets: isReview
            ? changesetsFingerprint(buildAssignationChangesets(changes))
            : null,
        }),
      );
      if (firstChangedSession) {
        dispatch({ type: Types.SelectedSessions, payload: firstChangedSession });
      }
      if (hasSkippedSections) {
        toast.warning(t("assignation-edit.proposal.toast.skipped-sections"), { autoClose: 8000 });
      }

      appliedOver.current = sections;
      setOpenedProposalRequest({ ...opened, status: "loaded", errorCode: null });
    };

    void open();

    return () => {
      isActive = false;
    };
    // `weeks` arrives with `sections` in the same link load; `t` and `dispatch` are stable.
  }, [
    proposalId,
    isReview,
    isResolving,
    hasRole,
    linkId,
    sections,
    scenario,
    origin,
    attempt,
    loadProposal,
    loadResources,
  ]);

  const retry = useCallback(() => {
    if (hasAbilitiesError) {
      retryApprovalFlowAbilities();
      return;
    }

    appliedOver.current = null;
    setAttempt(current => current + 1);
  }, [hasAbilitiesError]);

  return { status, errorCode: hasAbilitiesError ? null : (request?.errorCode ?? null), retry };
};
